import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Checkbox, Modal, Select } from "@playbookai/ui";
import {
  changeLifecycle,
  getLifecyclePreview,
  type ExecutionProfile,
} from "../../../api/admin-ai-profiles-api.js";
export interface LifecycleTarget {
  kind: "connections" | "profiles";
  row: { id: string; version: number; display_name: string };
  action: "disable" | "delete";
}
export function LifecycleDialog({
  open,
  target,
  queryKey,
  onSaved,
  onClose,
}: {
  /** Hoisted from the mount so the dialog can animate out (ADR-0091). */
  open: boolean;
  target: LifecycleTarget;
  profiles: ExecutionProfile[];
  queryKey: readonly string[];
  onSaved: () => void;
  onClose: () => void;
}) {
  const [replacements, setReplacements] = useState<Record<string, string>>({});
  const [draftIds, setDraftIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* The dialog is retained while it closes (ADR-0091), so it now outlives a
     single opening. A cancelled attempt must not leave its error, its chosen
     replacements or its ticked drafts waiting inside the next one. */
  useEffect(() => {
    if (!open) return;
    setError(null);
    setReplacements({});
    setDraftIds([]);
  }, [open]);
  const preview = useQuery({
    queryKey: [
      ...queryKey,
      target.kind,
      target.row.id,
      "impact",
      target.action,
    ],
    queryFn: () =>
      getLifecyclePreview(target.kind, target.row.id, target.action),
    staleTime: 0,
    // Also gated on `open`: a closed dialog must not re-read the impact.
    enabled: open,
  });
  const impact = preview.data;
  async function apply() {
    if (!impact) return;
    setSaving(true);
    setError(null);
    try {
      await changeLifecycle(
        target.kind,
        { ...target.row, version: impact.subject_version },
        target.action,
        {
          impact_token: impact.impact_token,
          replacements,
          ...(target.kind === "connections" && target.action === "delete"
            ? { delete_draft_profile_ids: draftIds }
            : {}),
        },
      );
      onSaved();
      onClose();
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Could not update configuration.",
      );
      setReplacements({});
      setDraftIds([]);
      await preview.refetch();
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal
      open={open}
      onClose={() => {
        if (!saving) onClose();
      }}
      title={`${target.action === "delete" ? "Delete" : "Disable"} ${target.row.display_name}?`}
      size="md"
    >
      <div className="grid min-w-0 gap-4 [&>*]:min-w-0">
        {(error || preview.error) && (
          <Alert
            tone="critical"
            title={
              error
                ? "That did not work"
                : "Couldn't check what depends on this"
            }
          >
            {error ?? preview.error?.message}
          </Alert>
        )}
        {preview.isFetching && <p role="status">Checking impact…</p>}
        {impact && (
          <>
            {impact.affected_profiles.length > 0 && (
              <p>
                Dependent profiles:{" "}
                {impact.affected_profiles.map((p) => p.display_name).join(", ")}
                . They will become unavailable.
              </p>
            )}
            {impact.explicit_defaults.map((slot) => (
              <Select
                key={slot.key}
                label={`Replacement for ${slot.key === "platform" ? "platform default" : slot.key}`}
                value={replacements[slot.key] ?? ""}
                onChange={(value) =>
                  setReplacements({ ...replacements, [slot.key]: value })
                }
                options={[
                  { value: "", label: "Choose a replacement" },
                  ...impact.candidates.map((p) => ({
                    value: p.id,
                    label: p.display_name,
                  })),
                ]}
              />
            ))}
            {impact.blocked && (
              <Alert tone="watch">
                {impact.blocked_reason ??
                  "Create and enable a replacement profile before continuing."}
              </Alert>
            )}
            {target.kind === "connections" &&
              target.action === "delete" &&
              impact.deletable_draft_profiles.map((profile) => (
                <Checkbox
                  key={profile.id}
                  label={`Also delete draft ${profile.display_name}`}
                  checked={draftIds.includes(profile.id)}
                  onChange={(checked) =>
                    setDraftIds((ids) =>
                      checked
                        ? [...ids, profile.id]
                        : ids.filter((id) => id !== profile.id),
                    )
                  }
                />
              ))}
            <p className="text-sm text-pb-text-muted">
              Active runs retain their pinned configuration. Saved audit history
              is retained.{" "}
              {target.action === "delete" &&
                "This configuration cannot be restored."}
            </p>
          </>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button
            /* A destructive confirm IS this modal's accent (§9); a delete must
               not read the same as a disable. */
            variant={target.action === "delete" ? "destructive" : "primary"}
            disabled={
              saving ||
              preview.isFetching ||
              !!preview.error ||
              !impact ||
              impact.blocked ||
              impact.explicit_defaults.some((slot) => !replacements[slot.key])
            }
            onClick={() => void apply()}
          >
            {saving
              ? "Saving…"
              : target.action === "delete"
                ? "Delete"
                : "Disable"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
